import { observer } from "mobx-react-lite";
import { type JSX } from "react";

import {
  BaseWorkoutItemsList,
  type RenderGroup,
  type RenderInsert,
  type RenderNormal,
} from "./BaseWorkoutItemsList";
import { useWorkoutEditStore } from "./WorkoutEditContext";
import { type WorkoutItemLayout } from "./WorkoutEditStore";

interface DetailedWorkoutItemsListProps {
  renderGroup: (args: RenderGroup) => JSX.Element;
  renderNormal: (args: RenderNormal) => JSX.Element;
  renderInsert?: (args: RenderInsert) => JSX.Element | null;
  renderDraft: (
    item: WorkoutItemLayout,
    level: number,
    index: number,
  ) => JSX.Element;
}

export const DetailedWorkoutItemsList = observer(
  function DetailedWorkoutItemsList(props: DetailedWorkoutItemsListProps) {
    const workoutEditStore = useWorkoutEditStore();

    function getPreviousItemId(
      prevItem: WorkoutItemLayout | null,
    ): string | undefined {
      if (!prevItem) {
        return undefined;
      }

      if (workoutEditStore.isDraft(prevItem.workoutItemId)) {
        return undefined;
      }

      return prevItem.workoutItemId;
    }

    function renderGroup(args: RenderGroup): JSX.Element {
      if (workoutEditStore.isDraft(args.item.workoutItemId)) {
        return props.renderDraft(args.item, args.level, args.index);
      }

      return props.renderGroup(args);
    }

    function renderNormal(args: RenderNormal): JSX.Element {
      if (workoutEditStore.isDraft(args.item.workoutItemId)) {
        return props.renderDraft(args.item, args.level, args.index);
      }

      return props.renderNormal(args);
    }

    function isSuperset(item: WorkoutItemLayout): boolean {
      return workoutEditStore.getIsWorkoutItemSuperset(item.workoutItemId);
    }

    return (
      <BaseWorkoutItemsList
        items={workoutEditStore.layout.items}
        renderGroup={renderGroup}
        renderNormal={renderNormal}
        renderInsert={props.renderInsert}
        isSuperset={isSuperset}
        getPreviousItemId={getPreviousItemId}
      />
    );
  },
);
